import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Navbar, Footer } from "../components/SiteChrome";
import {
  fetchCatalog,
  fetchAvailability,
  createCheckout,
  formatUSD,
} from "../lib/api";

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const BookingFlow = () => {
  const { tierId } = useParams();
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState({ services: [], bundles: [] });
  const [step, setStep] = useState(1); // 1:slot, 2:details, 3:redirecting
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCatalog().then(setCatalog).catch(() => {});
  }, []);

  const { offering, tier } = useMemo(() => {
    const all = [...catalog.services, ...catalog.bundles];
    for (const o of all) {
      const t = o.tiers.find((t) => t.id === tierId);
      if (t) return { offering: o, tier: t };
    }
    return { offering: null, tier: null };
  }, [catalog, tierId]);

  // Load slots when date changes
  useEffect(() => {
    if (!date) {
      setSlots([]);
      setTime(null);
      return;
    }
    const iso = toISODate(date);
    setSlotsLoading(true);
    setTime(null);
    fetchAvailability(iso)
      .then((r) => setSlots(r.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [date]);

  const validateForm = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Valid email required";
    // E.164-ish: must start with + and 7-15 digits
    if (!/^\+\d{7,15}$/.test(form.phone.replace(/\s/g, "")))
      e.phone = "Use E.164 format, e.g. +14155552671";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async () => {
    if (!validateForm() || !date || !time || !tier) return;
    setSubmitting(true);
    setStep(3);
    try {
      const payload = {
        tier_id: tier.id,
        slot_date: toISODate(date),
        slot_time: time,
        client: {
          ...form,
          phone: form.phone.replace(/\s/g, ""),
        },
        origin_url: window.location.origin,
      };
      const { checkout_url } = await createCheckout(payload);
      // Redirect to Stripe
      window.location.href = checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Could not start checkout";
      toast.error(msg);
      setStep(2);
      setSubmitting(false);
    }
  };

  if (!tier) {
    return (
      <div>
        <Navbar />
        <section className="max-w-3xl mx-auto px-6 py-24">
          <div className="text-[#5c5a56]">Loading booking…</div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <Link
          to={`/services/${offering?.slug}`}
          data-testid="back-to-service"
          className="inline-flex items-center gap-1 text-sm text-[#5c5a56] hover:text-[#0F1A36] mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" /> Back to {offering?.name || "service"}
        </Link>

        {/* Steps header */}
        <div className="flex items-center gap-6 mb-10 text-xs uppercase tracking-[0.2em] font-semibold">
          {[
            { i: 1, label: "Pick a slot" },
            { i: 2, label: "Your details" },
            { i: 3, label: "Payment" },
          ].map(({ i, label }) => (
            <div
              key={i}
              data-testid={`step-indicator-${i}`}
              className={`flex items-center gap-2 ${
                step === i ? "text-[#0F1A36]" : "text-[#5c5a56]/60"
              }`}
            >
              <span
                className={`w-6 h-6 inline-flex items-center justify-center border ${
                  step >= i ? "bg-[#0F1A36] text-[#f5f1e8] border-[#0F1A36]" : "border-[#5c5a56]/60"
                }`}
              >
                {i}
              </span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            {step === 1 && (
              <div
                data-testid="step-slot"
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 bg-white border border-[#d4cdb8] p-6 md:p-10"
              >
                <div className="lg:col-span-5">
                  <div className="overline mb-4">Choose a kickoff date</div>
                  <Calendar
                    data-testid="booking-calendar"
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < todayISO() || d.getDay() === 0 || d.getDay() === 6}
                    fromDate={todayISO()}
                  />
                  <p className="mt-4 text-xs text-[#5c5a56] max-w-xs">
                    All times in Pacific (PT). Weekdays only.
                  </p>
                </div>
                <div className="lg:col-span-7">
                  <div className="overline mb-4">Available times</div>
                  {!date && (
                    <div className="text-sm text-[#5c5a56]">
                      Pick a date to see available kickoff slots.
                    </div>
                  )}
                  {date && slotsLoading && (
                    <div className="flex items-center gap-2 text-sm text-[#5c5a56]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading slots…
                    </div>
                  )}
                  {date && !slotsLoading && slots.length === 0 && (
                    <div className="text-sm text-[#5c5a56]">
                      No available slots on this date. Try another.
                    </div>
                  )}
                  {!slotsLoading && slots.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`time-slot-${s.replace(/\s/g, "-")}`}
                          className={`slot-pill ${time === s ? "selected" : ""}`}
                          onClick={() => setTime(s)}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    data-testid="continue-to-details"
                    className="btn-primary mt-10"
                    disabled={!date || !time}
                    onClick={() => setStep(2)}
                  >
                    Continue <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div
                data-testid="step-details"
                className="bg-white border border-[#d4cdb8] p-6 md:p-10"
              >
                <div className="overline mb-4">Your details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="Full name"
                    name="name"
                    value={form.name}
                    onChange={(v) => setForm({ ...form, name: v })}
                    error={errors.name}
                    testId="input-name"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setForm({ ...form, email: v })}
                    error={errors.email}
                    testId="input-email"
                  />
                  <Field
                    label="Mobile phone (E.164, e.g. +14155552671)"
                    name="phone"
                    value={form.phone}
                    onChange={(v) => setForm({ ...form, phone: v })}
                    error={errors.phone}
                    testId="input-phone"
                  />
                  <Field
                    label="Company"
                    name="company"
                    value={form.company}
                    onChange={(v) => setForm({ ...form, company: v })}
                    testId="input-company"
                  />
                  <div className="md:col-span-2">
                    <Field
                      label="Anything we should know? (optional)"
                      name="notes"
                      textarea
                      value={form.notes}
                      onChange={(v) => setForm({ ...form, notes: v })}
                      testId="input-notes"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 mt-10">
                  <button
                    type="button"
                    data-testid="back-to-slot"
                    className="btn-outline"
                    onClick={() => setStep(1)}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    data-testid="proceed-to-payment"
                    className="btn-primary"
                    onClick={onSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Preparing…
                      </>
                    ) : (
                      <>
                        Pay {formatUSD(tier.price)} <ArrowUpRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>

                <p className="text-xs text-[#5c5a56] mt-6 max-w-xl">
                  You&rsquo;ll be redirected to Stripe&rsquo;s secure checkout to complete payment.
                  An SMS confirmation will be sent to your mobile after successful payment.
                </p>
              </div>
            )}

            {step === 3 && (
              <div
                data-testid="step-redirecting"
                className="bg-white border border-[#d4cdb8] p-10 md:p-16 text-center"
              >
                <Loader2 className="w-8 h-8 animate-spin text-[#C9A961] mx-auto mb-6" />
                <div className="font-display text-2xl font-semibold">
                  Redirecting to secure checkout…
                </div>
                <div className="text-sm text-[#5c5a56] mt-2">
                  Powered by Stripe. Please don&rsquo;t close this tab.
                </div>
              </div>
            )}
          </div>

          {/* Sidebar summary */}
          <aside data-testid="booking-summary" className="lg:col-span-4">
            <div className="bg-[#0F1A36] text-[#f5f1e8] p-8">
              <div className="overline !text-[#C9A961] mb-5">Order summary</div>
              <div className="font-display text-2xl font-semibold leading-tight">
                {offering.name}
              </div>
              <div className="text-sm text-[#f5f1e8]/60 mt-1">{tier.name}</div>

              <div className="mt-8 space-y-3 text-sm">
                <Row label="Tier" value={tier.name} />
                <Row label="Delivery" value={offering.delivery} />
                <Row
                  label="Kickoff"
                  value={date && time ? `${toISODate(date)} · ${time} PT` : "—"}
                />
                <Row label="Billing" value={tier.billing} />
              </div>

              <div className="my-8 border-t border-white/15" />

              <div className="flex items-baseline justify-between">
                <div className="overline !text-[#C9A961]">Due today</div>
                <div className="font-display text-3xl font-semibold">
                  {formatUSD(tier.price)}
                </div>
              </div>

              <div className="mt-6 text-xs text-[#f5f1e8]/50 leading-relaxed">
                Test mode. No real charge will occur. SMS confirmation sent on
                success.
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
};

const Field = ({ label, name, value, onChange, error, type = "text", testId, textarea }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#5c5a56] mb-2">
      {label}
    </span>
    {textarea ? (
      <textarea
        data-testid={testId}
        name={name}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-[#d4cdb8] rounded-none px-4 py-3 focus:border-[#C9A961] focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-colors font-body"
      />
    ) : (
      <input
        data-testid={testId}
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-[#d4cdb8] rounded-none px-4 py-3 focus:border-[#C9A961] focus:outline-none focus:ring-1 focus:ring-[#C9A961] transition-colors font-body"
      />
    )}
    {error && (
      <span data-testid={`${testId}-error`} className="block text-xs text-red-700 mt-1">
        {error}
      </span>
    )}
  </label>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-[#f5f1e8]/60">{label}</span>
    <span className="font-display text-right">{value}</span>
  </div>
);

export default BookingFlow;
